import { Injectable, OnDestroy } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { EMPTY, Observable, of, Subscription, throwError } from "rxjs";
import { catchError, map, mergeMap, switchMap, take, tap } from "rxjs/operators";
import { ContractService, SysParamService } from "src/app/api/services";
import { ArcHospiService } from "src/app/kiosk/services/arcHospi.service";
import { eKioskError } from "src/app/shared/models/api/constants";
import { AppState, getState } from "src/app/state/app.state";
import * as ArcHospiActions from '../../archospi/state/archospi.actions';
import { setErrorMsg } from "../../error/state/error.actions";
import { setContractBlocked } from "../../transaction-info/state/transaction-info.actions";
import { getSysParams, getSysParamsFailure, getSysParamsSuccess, setMaxDate, setMinDate, submitAdmissionDate, submitAdmissionDateFailure, submitAdmissionDateSuccess } from "./admission-date.action";
import moment from "moment";
import { ErrorLevel } from "../../error/error-level";

@Injectable()
export class admissionDateEffects implements OnDestroy{

	private state: AppState;
	private subs: Subscription[] = [];
	
	constructor(private actions$: Actions, private contractService: ContractService, private store: Store<AppState>, private arcHospiService: ArcHospiService, private SysParamService: SysParamService) {
		this.subs.push(getState(this.store).subscribe(state => this.state = state));
	}
	
	ngOnDestroy(): void {
		this.subs.forEach((_) => _.unsubscribe());
	}

	private IsBlocked(admissionDate: Date): Observable<boolean> {
		return this.contractService.contractIsContractBlocked({ admissionPlannedDate: admissionDate.toISOString() }).
			pipe(
				map((data) => {
					if (!data) {
						this.store.dispatch(setContractBlocked({ data: false }));
						return false;
					}
					else {
						this.store.dispatch(ArcHospiActions.contractBlocked());
						this.store.dispatch(setContractBlocked({ data: true }));
						const lang = this.state.transactionInfo.selectedLanguage.languageCode;
						this.store.dispatch(setErrorMsg({ data: data.kioskMessage["text" + lang], KioskError: eKioskError.CONTRACT_BLOCKED, noTranslation: true }));

						return true;
					}
				})
			);

	}


	private IsAdmissionDateValid(contractId: number, admissionDate: Date): Observable<boolean> {
		return this.contractService.contractValidateAdmissionDate({ admissionPlannedDate: admissionDate.toISOString() }).pipe(
			map((data) => {
				if (data.isOk) {
					return true;
				} else {

					if (data.errorMessage == eKioskError[eKioskError.ADMISSION_DATE_INTERVAL_ERROR])
						this.store.dispatch(ArcHospiActions.admissionDateIntervalError());
					else if (data.errorMessage == eKioskError[eKioskError.CONTRACT_NOT_YET_VALID])
						this.store.dispatch(ArcHospiActions.contractInvalid({ kioskError: eKioskError.CONTRACT_NOT_YET_VALID }));
					else
						this.store.dispatch(ArcHospiActions.contractInvalid({ kioskError: eKioskError.CONTRACT_EXPIRED }));

					this.store.dispatch(setErrorMsg({ data: null, KioskError: eKioskError[data.errorMessage], noTranslation: false, errorLevel:ErrorLevel.WARNING }));

					return false;
				}
			})
		);

	}



	/*
	 * 1. dispatch the admission date to the store & update the arc hospi
	 * 2. check if the contract is blocked.  If so: set the blocking message as state error message
	 * 3. if not blocked: validate the admission date towards the insurer allowed days in the future/past
	 */

	public submitAdmissionDate = createEffect(() => {
		return this.actions$.pipe(
			ofType(submitAdmissionDate),
			switchMap(({ admissionDate, contractId }) => {
				return this.arcHospiService.admissionPlannedDateEntered(admissionDate).pipe(
					map((arcHospi) => {
						if (arcHospi == null)
							throw new Error();

						this.store.dispatch(ArcHospiActions.admissionDateEnteredSuccess({ data: arcHospi }));
					}), 
					switchMap(() => {
						return this.IsBlocked(admissionDate)
					}),
					switchMap((isBlocked) => {
						if (isBlocked) {
							return of(submitAdmissionDateFailure());
						}
						else {
							return this.IsAdmissionDateValid(contractId, admissionDate);
						}
					}),
					switchMap((isValid) => {
						if (isValid)
							return of(submitAdmissionDateSuccess({ data: admissionDate }));
						else
							return of(submitAdmissionDateFailure());

					}),
					catchError((err) => {
						return of(submitAdmissionDateFailure());
					})
				)
			}
			)
		)
	});

	public getParams$ = createEffect(() =>
		this.actions$.pipe(
			ofType(getSysParams),
    		switchMap(() =>
    		  this.SysParamService.sysParamGetAsDto({
    		    Group: "KIOSK",
    		    Name: "ADMISSION_DATE_MIN_DATE",
    		    ApplicationType: 'H',
    		    DefaultValue: "3",
			}).pipe(
    		    tap(minDate => {
    		      const minVal = minDate?.value ?? "3";
    		      this.store.dispatch(
    		        setMinDate({ data: moment().subtract(minVal, "months").startOf('day').toDate() })
    		      );
    		    }),
    		    catchError(error => {
    		      return throwError(() => error); // not sure we cant this catch 
    		    })
    		  )
    		),
    		switchMap(() =>
    		  this.SysParamService.sysParamGetAsDto({
    		    Group: "KIOSK",
    		    Name: "ADMISSION_DATE_MAX_DATE",
    		    ApplicationType: 'H',
    		    DefaultValue: "3",
    		  }).pipe(
    		    tap(maxDate => {
    		      const maxVal = maxDate?.value ?? "3";
    		      this.store.dispatch(
    		        setMaxDate({ data: moment().add(maxVal, "months").startOf('day').toDate() })
    		      );
    		    }),
    		    catchError(error => {
    		      return throwError(() => error); // not sure we cant this catch 
    		    })
    		  )
    		),
    		map(() => getSysParamsSuccess()),
    		catchError(() => of(getSysParamsFailure()))
  		)
	);
}
